/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { isStrEmptyOrNull } from '../UtilityFunctions';

type UseLoanPayment = {
  loanPayment: UnknownLoanPayments;
  loanPayments: LoanPayment[];
  loadingLoanPayment: boolean;
  createLoanPayment: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  updateLoanPayment: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  reloadLoanPayment: () => void;
};

const useLoanPayment = (pathNameOrDate = '/list'): UseLoanPayment => {
  const { get } = useAbortController();
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();
  const queryClient = useQueryClient(); // Use the query client directly
  const path = useMemo(() => {
    const date =
      typeof pathNameOrDate === 'string'
        ? new Date(pathNameOrDate)
        : new Date(`${pathNameOrDate}`.length === 10 ? pathNameOrDate * 1000 : pathNameOrDate);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      let str = `${pathNameOrDate}`;
      if (isStrEmptyOrNull(str)) return '';
      if (str.startsWith('/')) return str;
      return '/' + str;
    }

    // Adjust to PST (UTC-8)
    let [month, day, year] = date
      .toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
      })
      .split('/');

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return `/${year}${month}${day}`;
  }, [pathNameOrDate]);

  const queryKey = useMemo(() => ['loanPayments', loanNumber, path, token], [loanNumber, path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        if (!path || !token) return { data: null };
        const request = {
          url: `live/loan/${loanNumber}/payment${path}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanPayment.response: `, response);
        return { data: response };
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error(`useLoanPayment.caught: ${error.message}`);
        }
        // Simulated fetch call for testing, TODO: Remove before public release.
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanPayment = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  // TODO: this function is a skeleton and needs to be updated to function properly
  const createLoanPayment = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/payment`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanPayment.response: `, response);
      reloadLoanPayment();
      return response;
    },
    [loanNumber, get, reloadLoanPayment, token],
  );

  const updateLoanPayment = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/payment${path}`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanPayment.response: `, response);
      reloadLoanPayment();
      return response;
    },
    [loanNumber, path, get, reloadLoanPayment, token],
  );

  return {
    loanPayment: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loanPayments:
      !isLoading && data && 'data' in data && data.data && Array.isArray(data.data) ? (data.data as LoanPayment[]) : [],
    loadingLoanPayment: isLoading,
    createLoanPayment,
    updateLoanPayment,
    reloadLoanPayment,
  };
};

export default useLoanPayment;
